import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Shield, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ClearableInput from "../components/ClearableInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AdminSubNav from "../components/AdminSubNav";
import { isSuperadmin as checkSuperadmin } from "../lib/permissions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatMembershipDuration = (createdAt) => {
  const start = new Date(createdAt);
  if (isNaN(start.getTime())) return "";
  const now = new Date();
  const diffMs = now - start;
  const days = Math.floor(diffMs / 86400000);
  if (days < 1) return "today";
  if (days < 31) return `${days} day${days > 1 ? "s" : ""}`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months} month${months > 1 ? "s" : ""}`;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  return remMonths > 0
    ? `${years} yr${years > 1 ? "s" : ""} ${remMonths} mo`
    : `${years} year${years > 1 ? "s" : ""}`;
};

const AdminUsers = ({ user, setUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [userSearch, setUserSearch] = useState("");
  const [organizationFilter, setOrganizationFilter] = useState("all"); // "all" or specific org name
  const [orgOptions, setOrgOptions] = useState([]); // unique organizations for filter dropdown
  const [editingUser, setEditingUser] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", organization: "", role: "user" });
  const [permissionUpdatingId, setPermissionUpdatingId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [freeAccessUpdatingId, setFreeAccessUpdatingId] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBin, setShowBin] = useState(false);

  const navigate = useNavigate();
  const isSuperadmin = checkSuperadmin(user);

  useEffect(() => {
    if (showBin) {
      fetchDeletedUsers();
    } else {
      fetchUsers();
    }
  }, [showBin]);

  const [allOrgs, setAllOrgs] = useState([]);
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/admin/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAllOrgs((resp.data || []).map((o) => o.organization));
      } catch (err) {
        console.error("Org list error:", err);
      }
    };
    fetchOrgs();
  }, [showCreateDialog]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetched = response.data || [];
      setUsers(fetched);

      // Build unique organization list for filter dropdown based on visible users
      const orgSet = new Set(
        fetched.map((u) => (u.organization || "Public").trim() || "Public"),
      );
      setOrgOptions(Array.from(orgSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })));
    } catch (error) {
      console.error("Fetch users error:", error);
      toast.error("Error loading users");
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedUsers = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/admin/recycle-bin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Fetch deleted users error:", error);
      toast.error("Error loading user bin");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API}/admin/users`,
        {
          email: newUser.email,
          name: newUser.name,
          organization: newUser.organization || "Public",
          role: newUser.role,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        "User created and welcome email sent (if email delivery is configured)",
      );
      setShowCreateDialog(false);
      setNewUser({ email: "", name: "", organization: "", role: "user" });
      fetchUsers();
    } catch (error) {
      console.error("Create user error:", error);
      const message = error.response?.data?.detail || "Error creating user";
      toast.error(message);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.patch(
        `${API}/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("User role updated");
      fetchUsers();
    } catch (error) {
      console.error("Update role error:", error);
      toast.error("Error updating role");
    }
  };

  const openEditNameDialog = (u) => {
    setEditingUser(u);
    setEditingName(u.name || "");
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    const trimmed = editingName.trim();
    if (!trimmed) {
      toast.error("Name cannot be empty");
      return;
    }

    const canManageOrg =
      isSuperadmin ||
      (user?.organization || "").trim().toLowerCase() ===
        "2ra-edition".toLowerCase();

    try {
      const token = localStorage.getItem("access_token");

      // Update name
      await axios.patch(
        `${API}/admin/users/${editingUser.id}/name`,
        { name: trimmed },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Update organization if allowed (superadmin or 2RA-Edition admin)
      if (canManageOrg) {
        const orgPayload = {
          organization:
            (editingUser.organization || "").trim() || "",
        };
        await axios.patch(
          `${API}/admin/users/${editingUser.id}/organization`,
          orgPayload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      // Update Stripe account ID (only superadmin/2RA-Edition can change, but
      // backend will enforce this as well)
      await axios.patch(
        `${API}/admin/users/${editingUser.id}/stripe-account`,
        {
          stripe_account_id:
            (editingUser.stripe_account_id || "").trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success("User updated");
      setEditingUser(null);
      setEditingName("");
      fetchUsers();
    } catch (error) {
      console.error("Update name/organization error:", error);
      const message =
        error.response?.data?.detail || "Error updating user profile";
      toast.error(message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  const toggleSelectUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllUsers = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map((u) => u.id));
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (selectedUserIds.length === 0) return;
    if (
      !window.confirm(
        `Delete ${selectedUserIds.length} user(s)? They will be moved to the bin for 60 days.`,
      )
    )
      return;

    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.delete(`${API}/admin/users/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: selectedUserIds },
      });
      const data = response.data || {};
      if (data.skipped?.length) {
        toast.warning(
          `Skipped ${data.skipped.length} user(s): ${data.skipped
            .map((s) => s.reason)
            .join(", ")}`,
        );
      }
      toast.success("Selected users moved to bin");
      setSelectedUserIds([]);
      fetchUsers();
    } catch (error) {
      console.error("Bulk delete users error:", error);
      toast.error(
        error.response?.data?.detail || "Error deleting selected users",
      );
    }
  };

  const handleRestoreUsers = async () => {
    if (selectedUserIds.length === 0) return;
    if (
      !window.confirm(
        `Restore ${selectedUserIds.length} user(s) from bin?`,
      )
    )
      return;

    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API}/admin/recycle-bin/users/restore`,
        { ids: selectedUserIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        "Users restored. Any restored admins have been converted to regular users with a new 7-day trial.",
      );
      setSelectedUserIds([]);
      fetchDeletedUsers();
    } catch (error) {
      console.error("Restore users error:", error);
      toast.error("Error restoring users");
    }
  };

  const handlePermanentDeleteUsers = async () => {
    if (selectedUserIds.length === 0) return;
    if (
      !window.confirm(
        `You are about to permanently delete ${selectedUserIds.length} user(s) from the recycle bin. This cannot be undone and you will not be able to restore them.`,
      )
    )
      return;

    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/admin/recycle-bin/users/permanent`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: selectedUserIds },
      });
      toast.success("Users permanently deleted");
      setSelectedUserIds([]);
      fetchDeletedUsers();
    } catch (error) {
      console.error("Permanent delete users error:", error);
      toast.error("Error permanently deleting users");
    }
  };

  const totalClients = users.filter((u) => u.role === "user").length;
  const totalAdmins = users.filter((u) => u.role === "admin").length;

  const filteredUsers = users.filter((u) => {
    // Search by email or name
    if (userSearch.trim()) {
      const term = userSearch.toLowerCase();
      const email = (u.email || "").toLowerCase();
      const name = (u.name || "").toLowerCase();
      const matchesText = email.includes(term) || name.includes(term);
      if (!matchesText) return false;
    }

    // Filter by organization
    if (organizationFilter !== "all") {
      const org = (u.organization || "Public").toLowerCase();
      return org === organizationFilter.toLowerCase();
    }

    return true;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortBy) return 0;

    const getString = (val) => (val || "").toString().toLowerCase();

    let aVal;
    let bVal;

    switch (sortBy) {
      case "email":
        aVal = getString(a.email);
        bVal = getString(b.email);
        break;
      case "name":
        aVal = getString(a.name);
        bVal = getString(b.name);
        break;
      case "role":
        aVal = getString(a.role);
        bVal = getString(b.role);
        break;
      case "organization":
        aVal = getString(a.organization || "Public");
        bVal = getString(b.organization || "Public");
        break;
      case "created_at":
      default:
        aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
        bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    }

    const comparison = aVal.localeCompare(bVal);
    return sortOrder === "asc" ? comparison : -comparison;
  });

  const [testAccountCount, setTestAccountCount] = useState(0);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // Fetch test account count on mount
  useEffect(() => {
    if (isSuperadmin) {
      const fetchTestCount = async () => {
        try {
          const resp = await axios.get(`${API}/admin/users/test-accounts`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
          });
          setTestAccountCount(resp.data?.count || 0);
        } catch (e) {
          // silently ignore
        }
      };
      fetchTestCount();
    }
  }, [isSuperadmin]);

  const handleCleanupTestAccounts = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete ${testAccountCount} test accounts? This cannot be undone.`)) return;
    setCleanupLoading(true);
    try {
      const resp = await axios.delete(`${API}/admin/users/test-accounts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
      });
      toast.success(resp.data?.message || "Test accounts deleted");
      setTestAccountCount(0);
      fetchUsers();
    } catch (e) {
      toast.error("Failed to delete test accounts");
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1
              className="text-xl font-semibold"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                Public Site
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="users" />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <h2
            className="text-2xl font-bold"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            {showBin ? "User Recycle Bin" : "Manage Users"}
          </h2>

          <div className="flex items-center gap-3">
            <div className="relative">
              <ClearableInput
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users (email or name)"
                className="w-64"
              />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500">Org:</span>
              <Select
                value={organizationFilter}
                onValueChange={(value) => setOrganizationFilter(value)}
              >
                <SelectTrigger className="w-40 h-8 text-xs">
                  <SelectValue placeholder="All organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {orgOptions.map((org) => (
                    <SelectItem key={org} value={org.toLowerCase()}>
                      {org}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isSuperadmin && (
              <button
                type="button"
                className={`text-xs px-3 py-1 rounded-full border ${
                  showBin
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300"
                }`}
                onClick={() => {
                  setSelectedUserIds([]);
                  setLoading(true);
                  setShowBin((prev) => !prev);
                }}
              >
                {showBin ? "Back to users" : "View user bin"}
              </button>
            )}

            {isSuperadmin && testAccountCount > 0 && (
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={handleCleanupTestAccounts}
                disabled={cleanupLoading}
                data-testid="cleanup-test-accounts-btn"
              >
                {cleanupLoading ? "Deleting..." : `Clean up test accounts (${testAccountCount})`}
              </Button>
            )}

            <Button
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => setShowCreateDialog(true)}
            >
              Create User
            </Button>
          </div>
        </div>

        {/* Totals summary */}
        <div className="mb-4 text-sm text-slate-600 flex gap-6">
          <span>
            Clients: <span className="font-semibold">{totalClients}</span>
          </span>
          <span>
            Administrators: <span className="font-semibold">{totalAdmins}</span>
          </span>
        </div>

        {!showBin && selectedUserIds.length > 0 && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-md px-4 py-2 mb-3">
            <span className="text-sm font-medium text-red-700">{selectedUserIds.length} user(s) selected</span>
            <Button
              variant="outline"
              size="sm"
              className="border-red-300 text-red-600 hover:bg-red-100"
              onClick={handleBulkDeleteUsers}
              data-testid="bulk-delete-btn"
            >
              Delete selected
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500"
              onClick={() => setSelectedUserIds([])}
            >
              Clear selection
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={
                        users.length > 0 &&
                        selectedUserIds.length === users.length
                      }
                      onChange={selectAllUsers}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    <button
                      type="button"
                      className="flex items-center gap-1 cursor-pointer select-none"
                      onClick={() => {
                        setSortBy("email");
                        setSortOrder((prev) =>
                          sortBy === "email" && prev === "asc" ? "desc" : "asc",
                        );
                      }}
                    >
                      Email
                      {sortBy === "email" && (
                        <span>{sortOrder === "asc" ? "e" : " ↓"}</span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    <button
                      type="button"
                      className="flex items-center gap-1 cursor-pointer select-none"
                      onClick={() => {
                        setSortBy("name");
                        setSortOrder((prev) =>
                          sortBy === "name" && prev === "asc" ? "desc" : "asc",
                        );
                      }}
                    >
                      Name
                      {sortBy === "name" && (
                        <span>{sortOrder === "asc" ? " ↑" : " ↓"}</span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    <button
                      type="button"
                      className="flex items-center gap-1 cursor-pointer select-none"
                      onClick={() => {
                        setSortBy("role");
                        setSortOrder((prev) =>
                          sortBy === "role" && prev === "asc" ? "desc" : "asc",
                        );
                      }}
                    >
                      Role
                      {sortBy === "role" && (
                        <span>{sortOrder === "asc" ? " ↑" : " ↓"}</span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    <button
                      type="button"
                      className="flex items-center gap-1 cursor-pointer select-none"
                      onClick={() => {
                        setSortBy("organization");
                        setSortOrder((prev) =>
                          sortBy === "organization" && prev === "asc" ? "desc" : "asc",
                        );
                      }}
                    >
                      Organization
                      {sortBy === "organization" && (
                        <span>{sortOrder === "asc" ? " ↑" : " ↓"}</span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    <button
                      type="button"
                      className="flex items-center gap-1 cursor-pointer select-none"
                      onClick={() => {
                        setSortBy("created_at");
                        setSortOrder((prev) =>
                          sortBy === "created_at" && prev === "asc"
                            ? "desc"
                            : "asc",
                        );
                      }}
                    >
                      Registered
                      {sortBy === "created_at" && (
                        <span>{sortOrder === "asc" ? " ↑" : " ↓"}</span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {sortedUsers.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-slate-50"
                    data-testid={`user-row-${u.id}`}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={selectedUserIds.includes(u.id)}
                        onChange={() => toggleSelectUser(u.id)}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900">{u.email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {u.name || "(no name)"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Shield
                          className={`w-4 h-4 mr-2 ${
                            u.role === "admin"
                              ? "text-orange-500"
                              : "text-slate-400"
                          }`}
                        />
                        <span className="text-sm text-slate-600">{u.role}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-testid={`user-org-${u.id}`}>
                      {u.organization || "Public"}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {/* Status column: Suspended / Free until / Active */}
                      {u.account_status === "suspended" ? (
                        "Suspended"
                      ) : u.free_access_until ? (
                        (() => {
                          const d = new Date(u.free_access_until);
                          const formatted = d.toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          });
                          return `Free until ${formatted}`;
                        })()
                      ) : (
                        "Active"
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap" data-testid={`user-registered-${u.id}`}>
                      {u.created_at ? (
                        <>
                          <div>
                            {new Date(u.created_at).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                          <div className="text-xs text-slate-400">
                            {formatMembershipDuration(u.created_at)}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      {/* Admin-only actions */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditNameDialog(u)}
                        data-testid={`edit-name-btn-${u.id}`}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Edit Name
                      </Button>
                      {isSuperadmin && u.id !== user.id && (
                        <Select
                          value={u.role}
                          onValueChange={(value) => handleRoleChange(u.id, value)}
                        >
                          <SelectTrigger
                            className="w-32"
                            data-testid={`role-select-${u.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {isSuperadmin && u.role === "admin" && (
                        <div className="inline-flex flex-col items-end space-y-1 mr-2 text-xs text-slate-600">
                          <label className="inline-flex items-center space-x-1">
                            <input
                              type="checkbox"
                              className="mr-1"
                              checked={!!u.can_see_stats}
                              disabled={permissionUpdatingId === u.id}
                              onChange={async (e) => {
                                try {
                                  setPermissionUpdatingId(u.id);
                                  const token = localStorage.getItem(
                                    "access_token",
                                  );
                                  await axios.patch(
                                    `${API}/admin/users/${u.id}/permissions`,
                                    {
                                      can_see_stats: e.target.checked,
                                      can_approve_submissions:
                                        !!u.can_approve_submissions,
                                    },
                                    {
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                      },
                                    },
                                  );
                                  toast.success("Permissions updated");
                                  fetchUsers();
                                } catch (err) {
                                  console.error(
                                    "Update permissions error:",
                                    err,
                                  );
                                  toast.error("Error updating permissions");
                                } finally {
                                  setPermissionUpdatingId(null);
                                }
                              }}
                            />
                            <span>Can see stats</span>
                          </label>
                          <label className="inline-flex items-center space-x-1">
                            <input
                              type="checkbox"
                              className="mr-1"
                              checked={!!u.can_approve_submissions}
                              disabled={permissionUpdatingId === u.id}
                              onChange={async (e) => {
                                try {
                                  setPermissionUpdatingId(u.id);
                                  const token = localStorage.getItem(
                                    "access_token",
                                  );
                                  await axios.patch(
                                    `${API}/admin/users/${u.id}/permissions`,
                                    {
                                      can_see_stats: !!u.can_see_stats,
                                      can_approve_submissions: e.target.checked,
                                    },
                                    {
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                      },
                                    },
                                  );
                                  toast.success("Permissions updated");
                                  fetchUsers();
                                } catch (err) {
                                  console.error(
                                    "Update permissions error:",
                                    err,
                                  );
                                  toast.error("Error updating permissions");
                                } finally {
                                  setPermissionUpdatingId(null);
                                }
                              }}
                            />
                            <span>Can approve submissions</span>
                          </label>
                        </div>
                      )}
                      {isSuperadmin && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-2"
                            disabled={statusUpdatingId === u.id}
                            onClick={async () => {
                              try {
                                setStatusUpdatingId(u.id);
                                const token = localStorage.getItem(
                                  "access_token",
                                );
                                const endpoint =
                                  u.account_status === "suspended"
                                    ? "unsuspend"
                                    : "suspend";
                                await axios.post(
                                  `${API}/admin/users/${u.id}/${endpoint}`,
                                  {},
                                  {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                    },
                                  },
                                );
                                toast.success(
                                  u.account_status === "suspended"
                                    ? "User unsuspended"
                                    : "User suspended",
                                );
                                fetchUsers();
                              } catch (err) {
                                console.error("Status update error:", err);
                                const message =
                                  err.response?.data?.detail ||
                                  "Error updating status";
                                toast.error(message);
                              } finally {
                                setStatusUpdatingId(null);
                              }
                            }}
                          >
                            {u.account_status === "suspended"
                              ? "Unsuspend"
                              : "Suspend"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-2"
                            disabled={freeAccessUpdatingId === u.id}
                            onClick={async () => {
                              const months = window.prompt(
                                "Grant free access for how many months? (1, 3, or 6)",
                                "1",
                              );
                              if (!months) return;
                              const m = parseInt(months, 10);
                              if (![1, 3, 6].includes(m)) {
                                toast.error("Please enter 1, 3, or 6");
                                return;
                              }
                              try {
                                setFreeAccessUpdatingId(u.id);
                                const token = localStorage.getItem(
                                  "access_token",
                                );
                                await axios.post(
                                  `${API}/admin/users/${u.id}/free-access`,
                                  { months: m },
                                  {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                    },
                                  },
                                );
                                toast.success(
                                  `Granted free access for ${m} month(s)`,
                                );
                                fetchUsers();
                              } catch (err) {
                                console.error("Free access error:", err);
                                const message =
                                  err.response?.data?.detail ||
                                  "Error granting free access";
                                toast.error(message);
                              } finally {
                                setFreeAccessUpdatingId(null);
                              }
                            }}
                          >
                            Grant Free Access
                          </Button>
                        </>
                      )}
                      {u.id !== user.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={async () => {
                            if (
                              !window.confirm(
                                "Are you sure you want to delete this user?",
                              )
                            )
                              return;
                            try {
                              const token = localStorage.getItem(
                                "access_token",
                              );
                              await axios.delete(
                                `${API}/admin/users/${u.id}`,
                                {
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                  },
                                },
                              );
                              toast.success("User deleted");
                              fetchUsers();
                            } catch (error) {
                              console.error("Delete user error:", error);
                              const message =
                                error.response?.data?.detail ||
                                "Error deleting user";
                              toast.error(message);
                            }
                          }}
                          data-testid={`delete-user-btn-${u.id}`}
                        >
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedUserIds.length > 0 && showBin && (
          <div className="mt-4 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-green-200 text-green-700 hover:bg-green-50"
              onClick={handleRestoreUsers}
            >
              Restore selected
            </Button>
            {isSuperadmin && (
              <Button
                variant="outline"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50"
                onClick={handlePermanentDeleteUsers}
              >
                Delete permanently
              </Button>
            )}
          </div>
        )}

        {editingUser && (
          <Dialog
            open={!!editingUser}
            onOpenChange={(open) => !open && setEditingUser(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>User Profile</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSaveName} className="space-y-4">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Email</div>
                  <div className="text-sm font-medium">{editingUser.email}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    placeholder="Enter user name"
                    data-testid="edit-name-input"
                  />
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Organization</div>
                  {(() => {
                    const canManageOrg =
                      isSuperadmin ||
                      (user?.organization || "").trim().toLowerCase() ===
                        "2ra-edition".toLowerCase();

                    if (!canManageOrg) {
                      return (
                        <div className="text-sm text-slate-700">
                          {editingUser.organization || "Public"}
                        </div>
                      );
                    }

                    return (
                      <div className="flex items-center gap-2">
                        <Select
                          value={(editingUser.organization || "Public").toLowerCase()}
                          onValueChange={(value) => {
                            const selected =
                              orgOptions.find(
                                (org) => org.toLowerCase() === value.toLowerCase(),
                              ) || value;
                            setEditingUser({
                              ...editingUser,
                              organization: selected,
                            });
                          }}
                        >
                          <SelectTrigger className="w-56 h-9 text-sm">
                            <SelectValue placeholder="Select organization" />
                          </SelectTrigger>
                          <SelectContent>
                            {orgOptions.map((org) => (
                              <SelectItem key={org} value={org.toLowerCase()}>
                                {org}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const name = window.prompt("Enter new organization name");
                            if (!name) return;
                            const trimmed = name.trim();
                            if (!trimmed) return;
                            const normalized =
                              trimmed.toLowerCase() === "public"
                                ? "Public"
                                : trimmed.toUpperCase();
                            setEditingUser({
                              ...editingUser,
                              organization: normalized,
                            });
                            setOrgOptions((prev) => {
                              if (prev.includes(normalized)) return prev;
                              return [...prev, normalized].sort((a, b) =>
                                a.localeCompare(b, undefined, { sensitivity: "base" }),
                              );
                            });
                          }}
                        >
                          Add
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem("access_token");
                              await axios.patch(
                                `${API}/admin/users/${editingUser.id}/organization`,
                                { organization: "" },
                                { headers: { Authorization: `Bearer ${token}` } },
                              );
                              toast.success("Organization cleared to Public");
                              setEditingUser({
                                ...editingUser,
                                organization: "Public",
                              });
                              fetchUsers();
                            } catch (err) {
                              console.error(
                                "Clear organization error:",
                                err,
                              );
                              const message =
                                err.response?.data?.detail ||
                                "Error clearing organization";
                              toast.error(message);
                            }
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Stripe account ID (optional, for payouts)</label>
                  <Input
                    value={editingUser.stripe_account_id || ""}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        stripe_account_id: e.target.value,
                      })
                    }
                    placeholder="acct_... (Stripe Connect account ID)"
                    data-testid="edit-stripe-account-input"
                  />
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Role</div>
                  <div className="text-sm text-slate-700">{editingUser.role}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Status</div>
                  <div className="text-sm text-slate-700">
                    {editingUser.account_status === "suspended"
                      ? "Suspended"
                      : editingUser.free_access_until
                      ? (() => {
                          const d = new Date(editingUser.free_access_until);
                          return `Free until ${d.toLocaleDateString()}`;
                        })()
                      : "Active"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Stripe account ID</div>
                  <div className="text-sm text-slate-700">
                    {editingUser.stripe_account_id || <span className="text-slate-400">(not set)</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Trial / Subscription</div>
                  <div className="text-sm text-slate-700 space-y-1">
                    <div>
                      <span className="font-medium">Trial end:</span>{" "}
                      {editingUser.trial_end_date
                        ? new Date(editingUser.trial_end_date).toLocaleDateString()
                        : "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Subscription status:</span>{" "}
                      {editingUser.subscription_status || "none"}
                    </div>
                    <div>
                      <span className="font-medium">Subscription end:</span>{" "}
                      {editingUser.subscription_end_date
                        ? new Date(editingUser.subscription_end_date).toLocaleDateString()
                        : "N/A"}
                    </div>
                  </div>
                </div>
                {/* Password section: visible to all admins, backend enforces org permissions */}
                <div className="border-t pt-4 mt-2">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold">Set New Password</h4>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => {
                          // Generate a strong password: 16 chars, mixed case + digits + symbols,
                          // guaranteed to include at least one of each class to satisfy any policy.
                          const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";       // no I, O
                          const lower = "abcdefghijkmnopqrstuvwxyz";       // no l
                          const digits = "23456789";                      // no 0, 1
                          const symbols = "!@#$%^&*-_=+";
                          const all = upper + lower + digits + symbols;
                          const pick = (set) => set[Math.floor(Math.random() * set.length)];
                          // Use crypto.getRandomValues for actual entropy where available
                          const cryptoPick = (set) => {
                            if (window.crypto && window.crypto.getRandomValues) {
                              const arr = new Uint32Array(1);
                              window.crypto.getRandomValues(arr);
                              return set[arr[0] % set.length];
                            }
                            return pick(set);
                          };
                          const chars = [
                            cryptoPick(upper),
                            cryptoPick(lower),
                            cryptoPick(digits),
                            cryptoPick(symbols),
                          ];
                          while (chars.length < 16) chars.push(cryptoPick(all));
                          // Shuffle so the guaranteed positions aren't always at the start
                          for (let i = chars.length - 1; i > 0; i--) {
                            const j = Math.floor(Math.random() * (i + 1));
                            [chars[i], chars[j]] = [chars[j], chars[i]];
                          }
                          const pwd = chars.join("");
                          setEditingUser({
                            ...editingUser,
                            _newPassword: pwd,
                            _confirmPassword: pwd,
                            _showGeneratedPassword: true,
                          });
                          // Best-effort clipboard copy — silently no-ops on insecure contexts
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            navigator.clipboard.writeText(pwd)
                              .then(() => toast.success("Strong password generated and copied to clipboard"))
                              .catch(() => toast.success("Strong password generated"));
                          } else {
                            toast.success("Strong password generated");
                          }
                        }}
                        data-testid="generate-password-btn"
                        title="Generate a 16-character random password and copy it to your clipboard"
                      >
                        Generate strong password
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">
                      Set a new password for this user. Org-admins can only change passwords within their organization.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        type={editingUser._showGeneratedPassword ? "text" : "password"}
                        placeholder="New password"
                        value={editingUser._newPassword || ""}
                        onChange={(e) => {
                          setEditingUser({
                            ...editingUser,
                            _newPassword: e.target.value,
                            _showGeneratedPassword: false,
                          });
                        }}
                        data-testid="new-password-input"
                      />
                      <Input
                        type={editingUser._showGeneratedPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        value={editingUser._confirmPassword || ""}
                        onChange={(e) => {
                          setEditingUser({
                            ...editingUser,
                            _confirmPassword: e.target.value,
                            _showGeneratedPassword: false,
                          });
                        }}
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <div className="mt-3 flex items-center justify-end gap-2 flex-wrap">
                      {editingUser._lastSavedPassword && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          onClick={async () => {
                            if (!editingUser?.email) {
                              toast.error("Cannot send: user email is missing");
                              return;
                            }
                            if (!window.confirm(`Send the new password to ${editingUser.email} via the in-app inbox? They'll see it the next time they log in.`)) return;
                            try {
                              const token = localStorage.getItem("access_token");
                              await axios.post(
                                `${API}/admin/messages`,
                                {
                                  to_emails: [editingUser.email],
                                  subject: "Your password was reset",
                                  body:
                                    `Hi ${editingUser.name || "there"},\n\n` +
                                    `An administrator has reset your password.\n\n` +
                                    `Your new password is:\n\n` +
                                    `    ${editingUser._lastSavedPassword}\n\n` +
                                    `Please log in with it as soon as possible and change it from your profile. ` +
                                    `For security, this message will only be readable in your in-app inbox — please do not share it.\n\n` +
                                    `If you did not expect this change, contact support immediately.`,
                                },
                                { headers: { Authorization: `Bearer ${token}` } },
                              );
                              toast.success(`Password sent to ${editingUser.email} via inbox`);
                              // Clear the cached password so the button hides
                              setEditingUser({ ...editingUser, _lastSavedPassword: "" });
                            } catch (err) {
                              console.error("Send inbox message error:", err);
                              toast.error(err.response?.data?.detail || "Failed to send via inbox");
                            }
                          }}
                          data-testid="send-password-inbox-btn"
                          title="Deliver the new password to the user via the in-app inbox (no email round-trip)"
                        >
                          Send via inbox
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        onClick={async () => {
                          const pwd = editingUser._newPassword || "";
                          const confirm = editingUser._confirmPassword || "";
                          if (!pwd || !confirm) {
                            toast.error("Please enter and confirm the new password");
                            return;
                          }
                          if (pwd !== confirm) {
                            toast.error("Passwords do not match");
                            return;
                          }
                          if (pwd.length < 8) {
                            toast.error("Password must be at least 8 characters");
                            return;
                          }
                          try {
                            const token = localStorage.getItem("access_token");
                            await axios.post(
                              `${API}/admin/users/${editingUser.id}/password`,
                              { new_password: pwd },
                              {
                                headers: { Authorization: `Bearer ${token}` },
                              },
                            );
                            toast.success("Password updated successfully");
                            // Cache the just-saved password so the "Send via inbox" button can use it
                            setEditingUser({
                              ...editingUser,
                              _newPassword: "",
                              _confirmPassword: "",
                              _lastSavedPassword: pwd,
                              _showGeneratedPassword: false,
                            });
                          } catch (err) {
                            console.error("Admin set password error:", err);
                            toast.error(
                              err.response?.data?.detail || "Error updating password",
                            );
                          }
                        }}
                        data-testid="save-password-btn"
                      >
                        Save new password
                      </Button>
                    </div>
                  </div>
                <div className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingUser(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Save
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <Input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  placeholder="user@example.com"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Enter user name"
                    data-testid="create-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Organization</label>
                  <Select
                    value={newUser.organization || "Public"}
                    onValueChange={(value) =>
                      setNewUser({ ...newUser, organization: value })
                    }
                  >
                    <SelectTrigger data-testid="create-organization-select">
                      <SelectValue placeholder="Public" />
                    </SelectTrigger>
                    <SelectContent>
                      {(allOrgs.length ? allOrgs : ["Public"]).map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Need a new one? Create it in the{" "}
                    <button
                      type="button"
                      className="text-orange-600 underline"
                      onClick={() => navigate("/admin/organizations")}
                      data-testid="create-org-shortcut"
                    >
                      Organizations tab
                    </button>
                    .
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) =>
                    setNewUser({ ...newUser, role: value })
                  }
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">
                A secure temporary password will be generated automatically and
                emailed to the user.
              </p>
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminUsers;
